import React from 'react';
import { type CopiedWhiteboardContent } from '../collaboration/Serialization';
import blocks, {
  type WireframeComponentBlock,
  type WireframeComponentBlockNames,
} from '../components/wireframeComponentBlock';
import type WhiteboardController from '../controllers/WhiteboardController';
import styles from '../styles.module.css';
import { labeledMenuComponent } from '../utils/stylus';
import Stylus from './Stylus';
import { ClipboardDataDrawingBrush } from './ClipboardDataDrawingBrush';
import { generatePNGFromClipboardData } from './ClipboardShapeIconGenerator';

class StylusBlock extends Stylus {
  controller: WhiteboardController;
  blockName: WireframeComponentBlockNames;
  block: WireframeComponentBlock;

  constructor({
    controller,
    blockName,
  }: {
    controller: WhiteboardController;
    blockName: WireframeComponentBlockNames;
  }) {
    super({ controller });
    this.controller = controller;
    this.blockName = blockName;
    this.block = blocks[blockName];
    if (!this.block.iconData) {
      console.warn(
        `Generating PNG thumbnail for ${blockName}. This should not occur in production.`
      );
      void generatePNGFromClipboardData(
        this.controller,
        this.block.shapeData.objects,
        `${this.blockName}.png`
      );
    }
  }

  activate() {
    this.controller.canvas.isDrawingMode = true;
    const clipboardData: CopiedWhiteboardContent = {
      objects: this.block.shapeData.objects,
      version: this.block.shapeData.version,
      whiteboardId: this.controller.whiteboardId!,
    };
    this.controller.canvas.freeDrawingBrush = new ClipboardDataDrawingBrush(
      clipboardData,
      this.controller,
      this
    );
  }

  stylusId() {
    return `block-${this.blockName}`;
  }

  renderIcon() {
    if (this.block.iconData === null) {
      return <p>Missing icon</p>;
    }
    return (
      <img
        data-testid={`wireframe-component-block-${this.blockName}`}
        src={this.block.iconData}
        style={{ width: '90px', maxHeight: '80px', objectFit: 'contain' }}
      ></img>
    );
  }

  toolButtonClass() {
    return `${styles.labeledToolButton} ${styles.labeledToolButtonWide}`;
  }

  menuComponent() {
    return labeledMenuComponent(this.renderIcon(), this.description());
  }

  description() {
    return this.block.displayName;
  }

  testid() {
    return `Stylus-block-${this.blockName}`;
  }

  cursor(): string {
    return 'crosshair';
  }
}

export const getBlockStyluses = (controller: WhiteboardController) => {
  const blockNames: WireframeComponentBlockNames[] = [
    'calendar',
    'contactDetailsForm',
    'contactUsForm',
    'dataTable',
    'dateRange',
    'errorBanner',
    'errorDialog',
    'fileUpload',
    'horizontalCard',
    'imageModal',
    'infoBanner',
    'login',
    'modal',
    'notificationBanner',
    'paymentForm',
    'searchAndFilter',
    'searchField',
    'settingsForm',
    'shareSheet',
    'signUp',
    'successBanner',
    'tooltip',
    'userCard',
    'verticalCard',
    'videoModal',
    'warningBanner',
    'warningDialog',
    'wizard',
    'playbackControls',
    'mapInput',
    'productCard',
    'dashboardCard',
    'dashboardGraph',
    'upgradeCard',
    'imageCarousel',
    'imageGallery',
  ];

  return blockNames.map(
    blockName =>
      new StylusBlock({
        controller,
        blockName,
      })
  );
};

export default StylusBlock;
